import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal } from 'antd';
import { I18n as I } from 'react-redux-i18n';
import ReduxHelper from 'lib/ReduxHelper';
import { BaseButton, LoaderOverlay, isNil } from 'vdlib';
import { DependentDataContext } from 'components/context/DependentData';
import GarageSelection from 'containers/garages/GarageSelectionContainer';
import NewGarage from 'components/garages/NewGarage';
import CSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';

const GarageModal = ({
  allowCreation,
  countries,
  garageData: propsGarageData,
  lang,
  loading,
  onHide,
  onSubmit,
  regions,
  show,
  taxOffices,
}) => {
  const [step, setStep] = React.useState(1);
  const [garageData, setGarageData] = React.useState(null);

  const { data } = React.useContext(DependentDataContext);
  const { newGarage: newGarageContext } = data || {};

  const handleResetState = React.useCallback(() => {
    setStep(1);
    setGarageData(propsGarageData);
    //setValid(false);
  }, [propsGarageData]);

  React.useEffect(() => {
    if (show) {
      setStep(isNil(propsGarageData) || !!propsGarageData?.ID ? 1 : 2);
      setGarageData(propsGarageData);
    } else {
      setGarageData(null);
    }
  }, [show]); //eslint-disable-line

  const handleHideModal = React.useCallback(() => {
    //handleResetState();
    onHide();
  }, [onHide]);

  const advance = React.useCallback(
    data => {
      if (step === 1) {
        setStep(2);
        setGarageData(propsGarageData?.ID ? null : propsGarageData);
      } else if (step === 2) {
        onSubmit(data);
      }
    },
    [onSubmit, step, propsGarageData]
  );

  const handleUpdateGarageData = React.useCallback(
    data => {
      setGarageData(!data ? null : { ...data, new: step === 2 });
      //setValid(!!data);
    },
    [step]
  );

  const handleSubmit = React.useCallback(() => {
    if (step === 1) return onSubmit(garageData);
    // garage creation, trigger submit, advance() will handle the rest
    newGarageContext.triggerSubmit();
  }, [newGarageContext, garageData, step, onSubmit]);

  return (
    <Modal
      closable={false}
      keyboard={false}
      maskClosable={false}
      footer={[
        step === 2 && (
          <BaseButton
            className="btn-sm btn-primary pull-left"
            onClick={handleResetState}
          >
            {I.t('CHOOSE_EXISTING')}
          </BaseButton>
        ),
        <BaseButton
          className="btn-sm btn-primary float-right ml-sm"
          disabled={loading || (step === 1 && !garageData)}
          onClick={handleSubmit}
        >
          {I.t('SAVE_INPUT')}
        </BaseButton>,
        <BaseButton
          className="btn-sm btn-default float-right"
          disabled={loading}
          onClick={handleHideModal}
        >
          {I.t('CANCEL')}
        </BaseButton>,
      ].filter(b => !!b)}
      onCancel={handleHideModal}
      title={I.t('GARAGE_CHOOSE')}
      visible={show}
      width={step === 2 ? '80%' : undefined}
    >
      <LoaderOverlay loading={loading} />
      <CSSTransitionGroup
        transitionName="page-transition"
        transitionAppear={true}
        transitionAppearTimeout={500}
        transitionEnterTimeout={500}
        transitionLeaveTimeout={500}
        component={'div'}
      >
        {step === 1 ? (
          <GarageSelection
            allowCreation={allowCreation === true}
            onAdvance={advance}
            onUpdateGarageData={handleUpdateGarageData}
            selectedGarage={step === 1 && garageData?.ID ? garageData : null}
          />
        ) : (
          <NewGarage
            advance={advance}
            data={garageData}
            countries={countries}
            embedded
            lang={lang}
            onSelectExisting={handleResetState}
            regions={regions}
            taxOffices={taxOffices}
            onSubmit={advance}
          />
        )}
      </CSSTransitionGroup>
      <div className="clear" />
    </Modal>
  );
};

GarageModal.propTypes = {
  allowCreation: PropTypes.bool,
  garageData: PropTypes.object,
  loading: PropTypes.bool,
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onSubmit: PropTypes.func,
};

const mapStateToProps = state =>
  ReduxHelper.mapStateWithLookups(
    {
      lang: state.i18n.locale,
    },
    state,
    'countries',
    'regions',
    'taxOffices'
  );

export default connect(mapStateToProps)(GarageModal);
